import {
  Component,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
} from '@angular/core';
import esCo from '@angular/common/locales/es-CO';
import { PageTitleService } from '../../shared/services';
import { Modal } from '../../shared/components/modal/modal';
import { formatDate, registerLocaleData } from '@angular/common';
import { PeopleAdd } from './people-add/people-add';
import { Client, ClientInput, ClientStatus } from '../../shared/interfaces';
import { CreatesByBusiness } from '../../shared/common/CreatesByBusiness.class';
import { ConfirmModal } from '../../shared/components/confirm-modal/confirm-modal';
import { Router, RouterLink } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
registerLocaleData(esCo);

@Component({
  selector: 'app-people',
  imports: [Modal, ConfirmModal, RouterLink],
  templateUrl: './people.html',
  styleUrl: './people.css',
  providers: [{ provide: LOCALE_ID, useValue: 'es-CO' }],
})
export class People
  extends CreatesByBusiness<Client | any, ClientInput>
  implements OnInit
{
  clients: Client[] = [];
  loading = false;
  page = 1;
  pageSize = 20;
  totalCount = 0;
  searchTerm = '';
  totalPages = 1;
  titleService = inject(PageTitleService);
  PeopleAddForm = PeopleAdd;
  protected endpoint = 'clients';
  confirmOpen = false;
  toDeleteId: string | null = null;
  toDeleteName: string | null = null;
  modalTitle = 'Nuevo cliente';
  modalInputs: Record<string, unknown> = {};
  router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private search$ = new Subject<string>();

  constructor() {
    super();
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((q) => {
          this.searchTerm = q;
          this.page = 1;
          this.load();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onSearch = (q: string) => this.search$.next(q);

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Clientes',
      subtitle: 'Las personas que reservan contigo y su historial de visitas',
    });
    this.load();
  }

  skeletonRows = Array(8).fill(0);

  private readonly statusLabels: Record<ClientStatus, string> = {
    active: 'Activo',
    inactive: 'Inactivo',
    banned: 'Bloqueado',
    prospect: 'Prospecto',
  };

  get paginationArray(): number[] {
    return Array(this.totalPages).fill(0);
  }

  fullName(c: Client): string {
    return `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
  }

  initials(c: Client): string {
    return `${c.firstName?.[0] ?? ''}${c.lastName?.[0] ?? ''}`.toUpperCase() || '?';
  }

  /** Only unusual statuses get a badge; "active" is the normal case */
  statusBadge(c: Client): string | null {
    if (!c.status || c.status === 'active') return null;
    return this.statusLabels[c.status] ?? null;
  }

  private daysSince(date: string): number {
    const ms = Date.now() - new Date(date).getTime();
    return Math.max(0, Math.floor(ms / 86_400_000));
  }

  /** "Hoy", "Hace 3 días", "Hace 2 meses"… or null when the client never visited */
  lastVisitLabel(c: Client): string | null {
    if (!c.lastVisitDate) return null;
    const days = this.daysSince(c.lastVisitDate);
    if (days === 0) return 'Hoy';
    if (days === 1) return 'Ayer';
    if (days < 7) return `Hace ${days} días`;
    if (days < 30) {
      const weeks = Math.floor(days / 7);
      return `Hace ${weeks} semana${weeks !== 1 ? 's' : ''}`;
    }
    if (days < 365) {
      const months = Math.floor(days / 30);
      return `Hace ${months} mes${months !== 1 ? 'es' : ''}`;
    }
    const years = Math.floor(days / 365);
    return `Hace ${years} año${years !== 1 ? 's' : ''}`;
  }

  /**
   * How recently the client came: recent (under 45 days), cooling (under 4 months)
   * or away. Helps spot who to invite back.
   */
  recency(c: Client): 'recent' | 'cooling' | 'away' | 'never' {
    if (!c.lastVisitDate) return 'never';
    const days = this.daysSince(c.lastVisitDate);
    if (days < 45) return 'recent';
    if (days < 120) return 'cooling';
    return 'away';
  }

  formatDay(date: string): string {
    return formatDate(date, "d 'de' MMMM 'de' y", 'es-CO');
  }

  onPageChange = (p: number) => {
    this.page = p;
    this.load();
  };

  load() {
    this.loading = true;
    this.preloader.show();
    this.businessService
      .getRecordsByBusinessId<Client>(
        this.businessId,
        {
          page: this.page,
          pageSize: this.pageSize,
          searchTerm: this.searchTerm,
          sort: 'createdAt',
          order: 'desc',
          isActive: true,
        },
        this.endpoint
      )
      .subscribe({
        next: (res: any) => {
          this.clients = res.clients ?? [];
          this.page = res.page;
          this.pageSize = res.pageSize;
          this.totalCount = res.totalCount;
          this.totalPages = res.totalPages;
          this.loading = false;
          this.preloader.hide();
        },
        error: (err) => {
          console.error(err);
          this.loading = false;
          this.preloader.hide();
        },
      });
  }

  onCreateClient() {
    this.open = true;
    this.modalTitle = 'Nuevo cliente';
    this.modalInputs = {
      entityId: null,
      endpoint: this.endpoint,
    };
  }

  onEditClient(userId: any) {
    this.modalTitle = 'Editar cliente';
    this.modalInputs = {
      entityId: userId,
      endpoint: this.endpoint,
    };
    this.open = true;
  }

  onViewClient(userId: any) {
    this.router.navigate(['/people/', userId]);
  }

  protected override afterCreate(_res: any): void {
    if (_res) {
      this.toastService.show('Cliente guardado', 'success');
      this.load();
    }
  }

  openDelete(row: Client) {
    this.toDeleteId = row.userId;
    this.toDeleteName = this.fullName(row) || null;
    this.confirmOpen = true;
  }

  onConfirmDelete() {
    if (!this.toDeleteId) return;
    this.deleteRecordById(this.toDeleteId);
  }

  protected override afterDelete(): void {
    this.toastService.show('Cliente eliminado', 'success');
    this.load();
  }

  protected override afterUpdate(): void {
    this.toastService.show('Cambios del cliente guardados', 'success');
    this.open = false;
    this.load();
  }
}
